import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

/**
 * Resets all users of a company account to its configured default signature role.
 *
 * @param companyAccountId - The ID of the company account whose users should be reset.
 * @returns The API response, whose message includes the number of updated users.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/reset_company_account_signature_roles_vadmin_company_account__company_account_uuid__reset_signature_roles_post
 */
export const resetSignatureRoles = async (companyAccountId: UUID) => {
  try {
    return await vestigasApi
      .post(ENDPOINT.COMPANY.ACCOUNT.RESET_SIGNATURE_ROLES(companyAccountId), {
        validate: { response: zodSuccessResponseSchema },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error resetting company account signature roles',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 400: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.resetSignatureRoles.error400,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.resetSignatureRoles.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
